import BaseComponent from 'scripts/BaseComponent.js';
import axios from 'axios';

export default class QuickShopProductOptions extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      tabBtn: '.js-tabBtn',
      tabPanel: '.js-tabPanel',
      productJson: '.js-quickShopProductJson',
      variantIdSelect: '.js-quickShopVariantSelect',
      productOptionBtn: '.js-quickShopProductOption',
      featuredImage: '.js-quickShopFeaturedImage',
      submitBtn: '.js-quickShopSubmit',
      price: '.js-quickShopInfoPrice',
      priceCompare: '.js-quickShopInfoPriceCompare',
      productLink: '.js-quickShopProductLink',
      relatedProductOptionBtn: '.js-quickShopRelatedProduct',
      imageCover: '.js-quickShopImageCover',
      imagesContainer: '.js-quickShopImagesContainer',
      valuesContainer: '.js-quickShopOptionValuesContainer',
      afterPayTarget: '.js-quickShopAfterpay',
    };

    this.dom = {
      $component: component,
      $tabBtns: this.$getAll(this.refs.tabBtn),
      $tabPanels: this.$getAll(this.refs.tabPanel),
      $productJson: document.querySelector(this.refs.productJson),
      $variantIdSelect: document.querySelector(this.refs.variantIdSelect),
      $submitBtn: document.querySelector(this.refs.submitBtn),
      $price: document.querySelector(this.refs.price),
      $priceCompare: document.querySelector(this.refs.priceCompare),
      $productLink: this.$get(this.refs.productLink),
      $relatedProductBtns: this.$getAll(this.refs.relatedProductOptionBtn),
      $imageCover: document.querySelector(this.refs.imageCover),
      $imagesContainer: document.querySelector(this.refs.imagesContainer),
      $valuesContainers: this.$getAll(this.refs.valuesContainer),
    };

    this.state = {
      productData: null,
      selectedOptions: {
        option1: null,
        option2: null,
        option3: null,
      },
      isSelectedClass: 'is-selected',
      variantNotAvailableClass: 'not-available',
      imageCoverRevealClass: 'reveal',
      imageCoverRemoveClass: 'remove',
      remountQuickShopEvent: 'remountquickshop',
      locale: null,
      currency: null,
    };

    this.mount();
    this.init();
    this.initAfterpay();
  }

  mount() {
    const { dom } = this;
    super.mount();

    this.setClickWithElement(dom.$tabBtns, this.tabClick);
    this.setClickWithElement(dom.$relatedProductBtns, this.relatedProductClick);
    this.singleOptionTabCheck();
    this.boxMaterialOptionCheck();
  }

  init() {
    this.parseJson();
    this.setSelectedOptions();
    this.mountOptionBtns();
  }

  initAfterpay() {
    this.setAfterpayProps();
    this.updateAfterpay();
  }

  parseJson() {
    const { dom, state } = this;
    state.productData = JSON.parse(dom.$productJson.innerHTML);
  }

  setSelectedOptions() {
    const { dom, state } = this;
    const defaultId = parseInt(dom.$variantIdSelect.value, 10);
    const variant = state.productData.variants.find(({ id }) => id === defaultId);

    state.selectedOptions.option1 = variant.option1;
    state.selectedOptions.option2 = variant.option2;
    state.selectedOptions.option3 = variant.option3;
  }

  tabClick(tabBtn) {
    const { dom } = this;
    const panelId = tabBtn.getAttribute('aria-controls');

    dom.$tabBtns.forEach((btn) => btn.setAttribute('aria-selected', false));
    tabBtn.setAttribute('aria-selected', true);

    dom.$tabPanels.forEach((panel) => panel.setAttribute('hidden', true));
    dom.$component.querySelector(`#${panelId}`).removeAttribute('hidden');
  }

  mountOptionBtns() {
    const { refs } = this;
    const optionBtns = this.$getAll(refs.productOptionBtn);
    this.setClickWithElement(optionBtns, this.optionSelect);
  }

  optionSelect(btn) {
    const { state } = this;
    const { optionValue, optionPosition } = btn.dataset;
    const optionKey = `option${optionPosition}`;

    this.updateSelectedOptionButtons(btn, optionPosition);
    state.selectedOptions[optionKey] = optionValue;
    this.updateSelectedVariant();
  }

  updateSelectedOptionButtons(selectedBtn, optionPosition) {
    const { state, refs } = this;

    const optionsPositionBtns = this.$getAll(`${refs.productOptionBtn}[data-option-position="${optionPosition}"]`);
    optionsPositionBtns.forEach((btn) => btn.classList.remove(state.isSelectedClass));

    selectedBtn.classList.add(state.isSelectedClass);
  }

  getSelectedVariant() {
    const { state } = this;
    const keys = Object.keys(state.selectedOptions);

    return state.productData.variants.find((variant) => (
      keys.every((key) => state.selectedOptions[key] === variant[key])
    ));
  }

  updateSelectedVariant() {
    const { dom, state } = this;
    const selectedVariant = this.getSelectedVariant();

    if (selectedVariant.featured_image) {
      this.updateFeaturedImage(selectedVariant.featured_image.src);
      this.setFirstSlide();
    }

    this.updateSubmitBtn(selectedVariant.available);
    dom.$variantIdSelect.value = selectedVariant.id;
    this.updatePrice();
    this.updateProductLink(`/products/${state.productData.handle}?variant=${selectedVariant.id}`);
  }

  updateFeaturedImage(src) {
    const { refs } = this;
    const featuredImages = document.querySelectorAll(refs.featuredImage);

    featuredImages.forEach((img) => img.setAttribute('src', `${src}&width=1024`));
  }

  setFirstSlide() {
    const swiper = window.theme.quickShopSwiper;
    if (!swiper) return;

    swiper.slideTo(1);
  }

  updateSubmitBtn(available) {
    const { dom, state } = this;

    if (available) {
      dom.$submitBtn.classList.remove(state.variantNotAvailableClass);
      dom.$submitBtn.removeAttribute('disabled');
    } else {
      dom.$submitBtn.classList.add(state.variantNotAvailableClass);
      dom.$submitBtn.setAttribute('disabled', true);
    }
  }

  updatePrice() {
    const { dom } = this;
    const select = dom.$variantIdSelect;
    const { price, compareAt } = select.options[select.selectedIndex].dataset;

    dom.$price.textContent = `$${price}`;
    dom.$priceCompare.textContent = `${compareAt}`;
  }

  async relatedProductClick(btn) {
    const { state } = this;
    if (btn.classList.contains(state.isSelectedClass)) return;

    try {
      this.enableImageCover();
      const { optionValue } = btn.dataset;
      const page = await this.fetchQuickShopTemplate(optionValue);
      this.parseQuickShopTemplate(page);
      this.updateRelatedProductButtons(btn);
      this.updateProductLink(optionValue);
    } catch (e) {
      console.error('Unable to switch product', e);
    }
  }

  enableImageCover() {
    const { dom, state } = this;

    dom.$imageCover.classList.remove(state.imageCoverRemoveClass);
    setTimeout(() => dom.$imageCover.classList.remove(state.imageCoverRevealClass), 0);
  }

  async fetchQuickShopTemplate(url) {
    try {
      const { data } = await axios.get(`${url}?view=quick-shop`);
      const parser = new DOMParser();
      const page = parser.parseFromString(data, 'text/html');

      return page;
    } catch (e) {
      console.error('Error featching related product quick shop data', e);
      throw e;
    }
  }

  parseQuickShopTemplate(page) {
    const { state } = this;

    this.updateImages(page);
    this.updateOptions(page);
    this.updateForm(page);
    this.updateJson(page);
    this.init();
    this.updateQuickShopState();

    this.dispatch(state.remountQuickShopEvent);
  }

  updateImages(page) {
    const { dom, refs } = this;

    const imagesContainer = page.querySelector(refs.imagesContainer);
    dom.$imagesContainer.innerHTML = imagesContainer.innerHTML;
    document.querySelector('[data-component="QuickShopProductImages"]').removeAttribute('data-mounted');
  }

  updateOptions(page) {
    const { dom } = this;

    dom.$valuesContainers.forEach((container) => this.updateValueContainer(container, page));
  }

  updateValueContainer(container, page) {
    const { refs } = this;
    const optionName = container.dataset.option;
    const updatedValues = page.querySelector(`${refs.valuesContainer}[data-option="${optionName}"]`);
    container.innerHTML = updatedValues.innerHTML;
  }

  updateForm(page) {
    const { dom, refs } = this;

    const variantIdSelect = page.querySelector(refs.variantIdSelect);
    dom.$variantIdSelect.innerHTML = variantIdSelect.innerHTML;
  }

  updateJson(page) {
    const { dom, refs } = this;

    const productJson = page.querySelector(refs.productJson);
    dom.$productJson.innerHTML = productJson.innerHTML;
  }

  updateQuickShopState() {
    const selectedVariant = this.getSelectedVariant();
    this.updateSubmitBtn(selectedVariant.available);
    this.updatePrice();
  }

  updateRelatedProductButtons(selectedBtn) {
    const { dom, state } = this;

    dom.$relatedProductBtns.forEach((btn) => btn.classList.remove(state.isSelectedClass));
    selectedBtn.classList.add(state.isSelectedClass);
  }

  updateProductLink(url) {
    const { dom } = this;
    dom.$productLink.setAttribute('href', url);
  }

  setAfterpayProps() {
    const { dom, state } = this;
    const { locale, currency } = dom.$component.dataset;

    state.locale = locale;
    state.currency = currency;
  }

  updateAfterpay() {
    const { state, refs } = this;

    window.Afterpay.createPlacements({
      targetSelector: refs.afterPayTarget,
      attributes: {
        amountSelector: refs.price,
        locale: state.locale,
        currency: state.currency,
        logoType: window.Afterpay.logoType.LOCKUP,
        lockupTheme: window.Afterpay.theme.lockup.BLACK,
        size: window.Afterpay.size.SM,
      },
    });
  }

  singleOptionTabCheck() {
    const { dom, refs } = this;
    const firstPanel = dom.$tabPanels[0];
    if (!firstPanel) return;

    const selector = `${refs.productOptionBtn}, ${refs.relatedProductOptionBtn}`;
    const firstPanelOptionsCount = firstPanel.querySelectorAll(selector).length;
    if (firstPanelOptionsCount > 1) return;

    dom.$tabBtns[1].click();
    dom.$tabBtns[0].remove();
    firstPanel.remove();
  }

  boxMaterialOptionCheck() {
    const { dom } = this;
    const firstTab = dom.$tabBtns[0];
    const { optionName } = firstTab.dataset;

    if (optionName === 'box material') {
      dom.$tabBtns[1].click();
      firstTab.remove();
      dom.$tabPanels[0].remove();
    }
  }
}
